import Link from 'next/link';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { StatusBadge } from '@repo/ui/components/status-badge';
import { TableCell, TableRow } from '@repo/ui/components/table';
import { cn } from '@repo/ui/lib/utils';
import { track } from '@/lib/analytics';
import { truncateAddress } from '@/lib/format/address';
import { formatPercent } from '@/lib/format/percent/percent';

import type { InvestorHolding } from '../types/api';
import { chainDisplayName } from '@/lib/chain-display-name';
import {
  formatTokenUnits,
  formatHoldingUnitPrice,
  formatHoldingValue,
  holdingDisplayName,
  holdingSymbol,
  KIND_LABELS,
  priceSourceLabel,
  tierLabel
} from '../utils/holding-format';
import {
  idOf,
  shareOf,
  valueOf,
  type RowFacts
} from '../utils/owned-positions';
import {
  LEDGER_ROW_CLASS,
  type useLensSelection
} from '@/components/lens-panel';

/** What every ledger row reads from the card: its facts and its state. */
export interface LedgerRowContext {
  facts: RowFacts;
  /** The holding whose detail drawer is open, if any. */
  detailId: string | null;
  onDetailChange: (rowId: string | null) => void;
  /** The donut segment a holding's row syncs to on hover, if any. */
  segmentSyncId: (holding: InvestorHolding) => string | null;
  rowProps: ReturnType<typeof useLensSelection>['rowProps'];
  dotColor: (segmentId: string | null) => string;
  /** Each member row wears exactly its outer arc's shade. */
  memberDotById: ReadonlyMap<string, { color: string; opacity: number }>;
}

/** One holding's row, and its detail drawer when open. */
export function HoldingRow({
  holding,
  indent,
  context
}: {
  holding: InvestorHolding;
  indent: boolean;
  context: LedgerRowContext;
}) {
  const { facts, detailId, memberDotById } = context;
  const rowId = idOf(facts, holding);
  const value = valueOf(facts, holding);
  const priced = value != null;
  const isVault = holding.kind === 'rowa' && holding.address != null;
  const expanded = detailId === rowId;
  const segmentId = context.segmentSyncId(holding);
  const unitSymbol = holdingSymbol(holding);

  return (
    <>
      <TableRow
        className={cn(LEDGER_ROW_CLASS, 'group')}
        {...(segmentId ? context.rowProps(segmentId) : {})}
      >
        <TableCell className={cn('min-w-52 py-3', indent ? 'pl-11' : 'pl-4')}>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-full"
              style={
                indent
                  ? {
                      backgroundColor:
                        memberDotById.get(rowId)?.color ??
                        'var(--color-rowa-border-subtle)',
                      opacity: memberDotById.get(rowId)?.opacity ?? 0.6
                    }
                  : { backgroundColor: context.dotColor(segmentId) }
              }
            />
            <div className="min-w-0">
              {isVault ? (
                <Link
                  href={`/vaults/${holding.address}`}
                  onClick={() =>
                    track('portfolio.vault_opened', {
                      vault: holding.address
                    })
                  }
                  className="text-rowa-body-sm text-rowa-text-primary hover:text-rowa-text-link inline-flex max-w-full items-center gap-1 truncate font-semibold"
                >
                  <span className="truncate">
                    {holdingDisplayName(holding)}
                  </span>
                  <ArrowUpRight
                    aria-hidden
                    className="size-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                  />
                </Link>
              ) : (
                <p className="text-rowa-body-sm text-rowa-text-primary truncate font-semibold">
                  {holdingDisplayName(holding)}
                </p>
              )}
              <p className="text-rowa-label-sm text-rowa-text-secondary truncate">
                {KIND_LABELS[holding.kind]} ·{' '}
                {chainDisplayName(holding.chainId)}
              </p>
            </div>
          </div>
        </TableCell>
        <TableCell className="text-rowa-body-sm text-rowa-text-primary text-right font-mono tabular-nums">
          {/* In the row's own currency: a ROWA vault's value is stated in
              its strategy currency, never stamped with a dollar sign. */}
          {priced ? (
            formatHoldingValue(value)
          ) : (
            <span className="text-rowa-text-tertiary">—</span>
          )}
        </TableCell>
        <TableCell className="text-rowa-body-sm text-rowa-text-primary text-right font-mono tabular-nums">
          {shareOf(facts, holding) != null ? (
            formatPercent(shareOf(facts, holding)!, { digits: 1 })
          ) : (
            <span className="text-rowa-text-tertiary">—</span>
          )}
        </TableCell>
        <TableCell className="pr-4">
          <div className="flex items-center justify-end gap-2">
            {!priced ? (
              <StatusBadge tone="warning">Unpriced</StatusBadge>
            ) : value.usd == null ? (
              <StatusBadge tone="neutral">No exchange rate</StatusBadge>
            ) : null}
            <button
              type="button"
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Hide' : 'Show'} details for ${holdingDisplayName(holding)}`}
              onClick={() => context.onDetailChange(expanded ? null : rowId)}
              className="text-rowa-text-tertiary hover:text-rowa-text-primary focus-visible:ring-rowa-border-accent rounded-rowa-full p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none"
            >
              <ChevronDown
                aria-hidden
                className={cn(
                  'size-4 transition-transform',
                  expanded && 'rotate-180'
                )}
              />
            </button>
          </div>
        </TableCell>
      </TableRow>
      {expanded ? (
        <TableRow className="bg-rowa-surface-inset/40 hover:bg-rowa-surface-inset/40">
          <TableCell
            colSpan={4}
            className={cn('py-3 pr-4', indent ? 'pl-11' : 'pl-4')}
          >
            <dl className="grid gap-x-8 gap-y-2 text-left sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-rowa-label-sm text-rowa-text-tertiary">
                  Units
                </dt>
                <dd className="text-rowa-body-sm text-rowa-text-primary font-mono tabular-nums">
                  {formatTokenUnits(holding)}
                  {unitSymbol ? ` ${unitSymbol}` : ''}
                </dd>
              </div>
              <div>
                <dt className="text-rowa-label-sm text-rowa-text-tertiary">
                  Unit price
                </dt>
                <dd className="text-rowa-body-sm text-rowa-text-primary">
                  {priced
                    ? `${formatHoldingUnitPrice(holding, facts.currencies, facts.fx)} · ${priceSourceLabel(holding)}`
                    : 'No price yet'}
                </dd>
              </div>
              <div>
                <dt className="text-rowa-label-sm text-rowa-text-tertiary">
                  Liquidity
                </dt>
                <dd className="text-rowa-body-sm text-rowa-text-primary">
                  {holding.liquidityTier
                    ? tierLabel(holding.liquidityTier)
                    : 'Not rated yet'}
                </dd>
              </div>
              <div>
                <dt className="text-rowa-label-sm text-rowa-text-tertiary">
                  Asset class
                </dt>
                <dd className="text-rowa-body-sm text-rowa-text-primary">
                  {holding.assetClass?.displayName ??
                    holding.assetClass?.slug ??
                    'Unclassified'}
                  {holding.address
                    ? ` · ${truncateAddress(holding.address)}`
                    : ''}
                </dd>
              </div>
            </dl>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
